import { Typography } from '@mui/material';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowBack from '@/assets/back.svg';
import Dialog from '@/components/ui/Dialog/Dialog';
import OnlyDesktop from '@/components/ui/OnlyDesktop/OnlyDesktop';
import OnlyMobile from '@/components/ui/OnlyMobile/OnlyMobile';
import { useHeatingAssistantContext } from '@/hooks/useHeatingAssistantContext';
import { useSourceContext } from '@/hooks/useSourceContext';

import {
  BackButton,
  Form,
  HeatingAssistantWrapper,
} from './HeatingAsistant.style';
import Start from './steps/Start/Start';

const Header = dynamic(() => import('./lib/Header'));
const Isolation = dynamic(() => import('./steps/Isolation/Isolation'));
const RoomSize = dynamic(() => import('./steps/RoomSize/RoomSize'));
const Temperature = dynamic(() => import('./steps/Temperature/Temperature'));
const HeatingKinds = dynamic(() => import('./steps/HeatingKinds/HeatingKinds'));
const Contact = dynamic(() => import('./steps/Contact/Contact'));
const SuccessScreen = dynamic(() =>
  import('./steps/SuccessScreen/SuccessScreen')
);
const DirectRequest = dynamic(() =>
  import('./steps/DirectRequest/DirectRequest')
);

const getInitialHeatingType = slug => {
  let type;
  if (slug?.includes('elektroheizungen')) {
    type = 'electro';
  } else if (
    slug?.some(value =>
      [
        'bautrocknung-bauheizungen',
        'heizzentralen',
        'heissluftgeblaese',
        'oelheizungen',
      ].includes(value)
    )
  ) {
    type = 'oil';
  } else if (slug?.includes('gasheizungen')) {
    type = 'gas';
  }

  const heatingType = { oil: false, electro: false, gas: false };
  if (type) {
    heatingType[type] = true;
  }

  return heatingType;
};

/**
 * Step wizard to make an order for heating machine.
 */

const HeatingAssistant = () => {
  const {
    heatingAssistantData,
    updateHeatingAssistantData,
  } = useHeatingAssistantContext();

  const { activeStep } = heatingAssistantData;
  const openDialog = activeStep !== 0 ? true : false;
  const { source } = useSourceContext();

  const router = useRouter();
  const { slug } = router.query;

  // Sets heating type to initial value based on url slug, updates when slug changes
  useEffect(() => {
    const initialHeatingType = getInitialHeatingType(slug);
    updateHeatingAssistantData({ heatingType: initialHeatingType });
  }, [slug, updateHeatingAssistantData]);

  const renderBack = () => (
    <BackButton
      data-test-subject="back-button"
      onClick={() => updateHeatingAssistantData({ activeStep: 0 })}>
      <ArrowBack />
      <FormattedMessage id="heating_form_cancel">
        {txt => (
          <Typography color="primary.main" variant="body1" component="span">
            {txt}
          </Typography>
        )}
      </FormattedMessage>
    </BackButton>
  );

  const renderActiveStep = activeStep => {
    switch (activeStep) {
      case 0:
        return (
          <Start
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 1:
        return (
          <Isolation
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 2:
        return (
          <RoomSize
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 3:
        return (
          <Temperature
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 4:
        return (
          <HeatingKinds
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 5:
        return (
          <SuccessScreen
            leadReference={source.leadReference}
            data={heatingAssistantData}
          />
        );
      case 6:
        return (
          <Contact
            source={source}
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      case 7:
        return (
          <DirectRequest
            source={source}
            updateData={updateHeatingAssistantData}
            data={heatingAssistantData}
          />
        );
      default:
        return '';
    }
  };

  const renderHeader = () => {
    if ([1, 2, 3, 4].includes(heatingAssistantData.activeStep)) {
      return (
        <Header
          data={heatingAssistantData}
          updateData={updateHeatingAssistantData}
        />
      );
    }
    return '';
  };

  return (
    <HeatingAssistantWrapper data-test-subject="heating-assistant-form">
      <OnlyDesktop>{activeStep !== 0 && renderBack()}</OnlyDesktop>
      <Form isStep0={activeStep === 0}>
        <OnlyMobile unmountOnHide>
          <div>
            {activeStep === 0 && renderActiveStep(activeStep)}
            {activeStep !== 0 && (
              <Dialog open={openDialog} fullScreen mobileMenu={false}>
                <>
                  {renderHeader()}
                  {renderActiveStep(activeStep)}
                  {renderBack()}
                </>
              </Dialog>
            )}
          </div>
        </OnlyMobile>
        <OnlyDesktop unmountOnHide>
          <div>
            {renderHeader()}
            {renderActiveStep(activeStep)}
          </div>
        </OnlyDesktop>
      </Form>
    </HeatingAssistantWrapper>
  );
};

export default HeatingAssistant;
